/**
 * React-compatible Vuex facade backed by per-property Jotai atoms.
 *
 * Port of createJotaiVuexFacadeForVue.ts — identical store semantics
 * (modules, namespaced commit/dispatch, Immer-based mutations, plugins,
 * subscribe) but uses React's useSyncExternalStore for re-renders instead
 * of Vue's reactive().
 *
 * Escape hatch — dynamic atoms:
 * When a mutation writes a key to the root state slice that was not
 * declared in the initial `state` atom map, the facade automatically
 * registers a new Jotai atom for that key so the value survives the
 * next buildCombinedState() call.  This prevents silent data loss when
 * the serializer sends fields that the caller forgot to declare up-front
 * (e.g. `base_orders` → `baseOrders`).  A dev-mode warning is logged so
 * the omission can be caught and fixed at the declaration site.
 * 
 */

import { atom } from "jotai/vanilla";
import { useAtomValue } from "jotai/react";
import { produce, setAutoFreeze } from "immer";
// @ts-ignore TS(7016): Could not find a declaration file for module 'lodash.isequal'
import isEqual from "lodash.isequal";

type WritableAtom<T = unknown> = { init: T };
type AtomMap = Record<string, WritableAtom>;

type JotaiStore = {
  get(a: WritableAtom | { read: unknown }): unknown;
  set<T>(a: WritableAtom<T>, value: T): void;
  sub(a: WritableAtom | { read: unknown }, callback: () => void): () => void;
};

type ModuleConfig = {
  namespaced?: boolean;
  state(): AtomMap;
  mutations?: Record<string, (state: unknown, payload?: unknown) => void>;
  getters?: Record<
    string,
    (state: unknown, getters: unknown, rootState: unknown, rootGetters: unknown) => unknown
  >;
  actions?: Record<string, (ctx: unknown, payload?: unknown) => unknown>;
};

type MutationDescriptor = { type: string; payload?: unknown };
type SubscribeCallback = (mutation: MutationDescriptor, state: Record<string, unknown>) => void;

type FacadeOptions = {
  jotaiStore: JotaiStore;
  state: AtomMap;
  stateKey?: string;
  getters?: Record<string, (state: unknown, getters?: unknown) => unknown>;
  mutations?: Record<string, (state: unknown, payload?: unknown) => void>;
  actions?: Record<string, (ctx: unknown, payload?: unknown) => unknown>;
  modules?: Record<string, ModuleConfig>;
  plugins?: ((store: ReactFacade) => void)[];
};

type ReactFacade = {
  state: Record<string, unknown>;
  getters: Record<string, unknown>;
  commit(name: string, payload?: unknown, options?: { root?: boolean }): void;
  dispatch(name: string, payload?: unknown, options?: { root?: boolean }): Promise<unknown>;
  subscribe(fn: SubscribeCallback): () => void;
  useStore(): ReactFacade;
};

export function createJotaiVuexFacadeForReact({
  jotaiStore,
  state: rootAtoms,
  stateKey = "cartState",
  getters: rootGetterDefs = {},
  mutations: rootMutationDefs = {},
  actions: rootActionDefs = {},
  modules = {},
  plugins = []
}: FacadeOptions): ReactFacade {
  setAutoFreeze(false);

  const subscribers: SubscribeCallback[] = [];

  // ── dynamic atom registry (escape hatch) ─────────────────────────────
  // Keys written by mutations that were not pre-declared in rootAtoms get
  // their own atom here so the value is not lost on the next read-back.
  // Ideally all fields are declared up-front; this is just a safety net.
  const dynamicRootAtoms: AtomMap = {};

  function ensureDynamicAtom(key: string, initialValue: unknown): void {
    if (key in rootAtoms || key in dynamicRootAtoms) return;

    console.warn(
      `[jotai-facade-react] Auto-registering dynamic atom for undeclared state key "${key}". ` +
      `Add it to the state atom map to silence this warning.`
    );

    const a = atom(initialValue) as WritableAtom;
    dynamicRootAtoms[key] = a;
    // Subscribe the new atom to $combined so components re-render when it changes.
    allAtoms.push(a);
    jotaiStore.sub(a, refresh);
  }

  // ── helpers ──────────────────────────────────────────────────────────

  function readAtomMap(map: AtomMap): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, a] of Object.entries(map)) out[k] = jotaiStore.get(a);
    return out;
  }

  function buildCombinedState(): Record<string, unknown> {
    const rootState = readAtomMap(rootAtoms);
    // Merge in any dynamically registered atoms so getters and components
    // always see the full state regardless of declaration order.
    for (const [k, a] of Object.entries(dynamicRootAtoms)) {
      rootState[k] = jotaiStore.get(a);
    }
    const combined: Record<string, unknown> = { [stateKey]: rootState };
    for (const [modName, mod] of Object.entries(modules)) {
      combined[modName] = readAtomMap(mod.state());
    }
    return combined;
  }

  function flushAtomMap(
    map: AtomMap,
    prev: Record<string, unknown>,
    next: Record<string, unknown>
  ): void {
    for (const [k, a] of Object.entries(map)) {
      if (next[k] !== prev[k]) jotaiStore.set(a, next[k]);
    }
  }

  function flushRootStateSlice(
    prev: Record<string, unknown>,
    next: Record<string, unknown>
  ): void {
    // Flush declared atoms.
    flushAtomMap(rootAtoms, prev, next);
    // For any key present in next but absent from rootAtoms, auto-register a
    // dynamic atom (escape hatch) and flush its initial value.
    for (const key of Object.keys(next)) {
      if (key in rootAtoms) continue;
      ensureDynamicAtom(key, next[key]);
      const a = dynamicRootAtoms[key];
      if (next[key] !== prev[key]) jotaiStore.set(a as WritableAtom, next[key]);
    }
    // Also flush any already-registered dynamic atoms whose value changed.
    for (const [k, a] of Object.entries(dynamicRootAtoms)) {
      if (!(k in next) || next[k] === prev[k]) continue;
      jotaiStore.set(a, next[k]);
    }
  }

  function flushCombinedState(
    prev: Record<string, unknown>,
    next: Record<string, unknown>
  ): void {
    flushRootStateSlice(
      prev[stateKey] as Record<string, unknown>,
      next[stateKey] as Record<string, unknown>
    );
    for (const [modName, mod] of Object.entries(modules)) {
      flushAtomMap(
        mod.state(),
        prev[modName] as Record<string, unknown>,
        next[modName] as Record<string, unknown>
      );
    }
  }

  // ── derived atom for subscriptions ───────────────────────────────────

  const allAtoms: WritableAtom[] = [
    ...Object.values(rootAtoms),
    ...Object.values(modules).flatMap(m => Object.values(m.state()))
  ];

  // Debug: build atom → name reverse lookup
  const atomNames = new Map<WritableAtom, string>();
  for (const [k, a] of Object.entries(rootAtoms)) atomNames.set(a as WritableAtom, k);
  for (const [modName, mod] of Object.entries(modules)) {
    for (const [k, a] of Object.entries(mod.state())) {
      atomNames.set(a as WritableAtom, `${modName}/${k}`);
    }
  }

  // Track previous atom values for deep equality comparison.
  // Only increment the version when at least one atom's value has actually
  // changed (deep equality), preventing spurious re-renders when immer
  // produces a new reference for logically identical data.
  let $combinedVersion = 0;
  const $prevAtomValues = new Map<WritableAtom, unknown>();

  const $combined = atom((get: (a: unknown) => unknown) => {
    let anyChanged = false;
    for (const a of allAtoms) {
      const next = get(a);
      if (!$prevAtomValues.has(a) || !isEqual($prevAtomValues.get(a), next)) {
        anyChanged = true;
        // eslint-disable-next-line no-console
        console.debug(`[jotai] atom changed: ${atomNames.get(a) ?? "unknown"}`);
        $prevAtomValues.set(a, next);
      }
    }
    if (anyChanged) $combinedVersion++;
    return $combinedVersion;
  });

  // ── getter cache ────────────────────────────────────────────────────

  let getterCache: Record<string, unknown> = {};
  let stateSnapshot: Record<string, unknown> = {};

  function makeModuleGetterProxy(modName: string): Record<string, unknown> {
    return new Proxy({} as Record<string, unknown>, {
      get(_target, prop: string) {
        return getterCache[`${modName}/${prop}`];
      }
    });
  }

  function recomputeGetters(): void {
    const combined = buildCombinedState();

    for (const [name, fn] of Object.entries(rootGetterDefs)) {
      try {
        getterCache[name] = fn(combined, getterCache);
      } catch { /* getter may fail during init */ }
    }
    for (const [modName, mod] of Object.entries(modules)) {
      if (!mod.getters) continue;
      const modState = combined[modName];
      const modGetters = makeModuleGetterProxy(modName);
      for (const [name, fn] of Object.entries(mod.getters)) {
        const key = mod.namespaced ? `${modName}/${name}` : name;
        try {
          getterCache[key] = fn(modState, modGetters, combined, getterCache);
        } catch { /* getter may fail during init */ }
      }
    }
  }

  function refresh(): void {
    recomputeGetters();
    stateSnapshot = buildCombinedState();
  }

  refresh();

  jotaiStore.sub($combined, refresh);

  // ── commit / dispatch ────────────────────────────────────────────────

  function resolveCommit(
    name: string
  ): { fn: (s: unknown, p?: unknown) => void; scope: "root" | string } | null {
    if (rootMutationDefs[name]) return { fn: rootMutationDefs[name], scope: "root" };
    const slash = name.indexOf("/");
    if (slash !== -1) {
      const modName = name.slice(0, slash);
      const mutName = name.slice(slash + 1);
      const mod = modules[modName];
      if (mod?.mutations?.[mutName]) return { fn: mod.mutations[mutName], scope: modName };
    }
    return null;
  }

  function resolveDispatch(
    name: string
  ): { fn: (ctx: unknown, p?: unknown) => unknown; scope: "root" | string } | null {
    if (rootActionDefs[name]) return { fn: rootActionDefs[name], scope: "root" };
    const slash = name.indexOf("/");
    if (slash !== -1) {
      const modName = name.slice(0, slash);
      const actName = name.slice(slash + 1);
      const mod = modules[modName];
      if (mod?.actions?.[actName]) return { fn: mod.actions[actName], scope: modName };
    }
    if (rootMutationDefs[name]) return { fn: rootMutationDefs[name], scope: "root" };
    return null;
  }

  function execMutation(name: string, payload?: unknown): void {
    const resolved = resolveCommit(name);
    if (!resolved) {
      console.warn(`[jotai-facade-react] Unknown mutation: ${name}`);
      return;
    }

    if (resolved.scope === "root") {
      const prev = buildCombinedState();
      const next = produce(prev, (draft: Record<string, unknown>) => {
        resolved.fn(draft, payload);
      });
      flushCombinedState(prev, next);
    } else {
      const modAtoms = modules[resolved.scope]!.state();
      const prev = readAtomMap(modAtoms);
      const next = produce(prev, (draft: Record<string, unknown>) => {
        resolved.fn(draft, payload);
      });
      flushAtomMap(modAtoms, prev, next);
    }
  }

  function makeModuleContext(modName: string) {
    const mod = modules[modName]!;
    return {
      get state() { return readAtomMap(mod.state()); },
      get getters() {
        if (!mod.namespaced) return getterCache;
        return new Proxy({} as Record<string, unknown>, {
          get(_, prop: string) {
            return getterCache[`${modName}/${prop}`];
          }
        });
      },
      get rootState() { return buildCombinedState(); },
      get rootGetters() { return getterCache; },
      commit(name: string, payload?: unknown, opts?: { root?: boolean }) {
        const fullName = opts?.root ? name : `${modName}/${name}`;
        facade.commit(fullName, payload);
      },
      dispatch(name: string, payload?: unknown, opts?: { root?: boolean }) {
        const fullName = opts?.root ? name : `${modName}/${name}`;
        return facade.dispatch(fullName, payload);
      }
    };
  }

  function makeRootContext() {
    return {
      get state() { return buildCombinedState(); },
      get getters() { return getterCache; },
      commit(name: string, payload?: unknown, _opts?: { root?: boolean }) {
        facade.commit(name, payload);
      },
      dispatch(name: string, payload?: unknown, _opts?: { root?: boolean }) {
        return facade.dispatch(name, payload);
      }
    };
  }

  function notifySubscribers(type: string, payload?: unknown): void {
    const combined = buildCombinedState();
    for (const fn of subscribers) fn({ type, payload }, combined);
  }

  // ── facade ───────────────────────────────────────────────────────────

  const facade: ReactFacade = {
    get state() { return stateSnapshot; },

    getters: new Proxy({} as Record<string, unknown>, {
      get(_, name: string) {
        return getterCache[name];
      }
    }),

    commit(name: string, payload?: unknown, _options?: { root?: boolean }) {
      execMutation(name, payload);
      notifySubscribers(name, payload);
    },

    dispatch(name: string, payload?: unknown, _options?: { root?: boolean }) {
      const resolved = resolveDispatch(name);
      if (!resolved) {
        console.warn(`[jotai-facade-react] Unknown action: ${name}`);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        if (resolved.scope === "root") {
          if (rootActionDefs[name]) {
            resolve(rootActionDefs[name](makeRootContext(), payload));
          } else {
            execMutation(name, payload);
            notifySubscribers(name, payload);
            resolve(undefined);
          }
        } else {
          const ctx = makeModuleContext(resolved.scope);
          resolve(resolved.fn(ctx, payload));
        }
      });
    },

    subscribe(fn: SubscribeCallback) {
      subscribers.push(fn);
      return () => {
        const idx = subscribers.indexOf(fn);
        if (idx !== -1) subscribers.splice(idx, 1);
      };
    },

    useStore() {
      useAtomValue($combined, { store: jotaiStore as never });
      return facade;
    }
  };

  for (const plugin of plugins) plugin(facade);

  return facade;
}
